"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { Building, ChevronRight, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { UserProfile } from "./user-profile";
import { APP_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

type ActiveView =
  | "overview"
  | "properties"
  | "analytics"
  | "contacts"
  | "areas"
  | "agent-profile"
  | "settings"
  | "blogs"
  | "drafts"
  | "users";

interface SidebarItem {
  id: ActiveView;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface SidebarContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isMobile: boolean;
  toggle: () => void;
}

// ---------------------------------------------------------------------------
// context
// ---------------------------------------------------------------------------

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebarContext() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within SidebarProvider");
  }
  return context;
}

// ---------------------------------------------------------------------------
// sidebar provider
// ---------------------------------------------------------------------------

interface SidebarProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function SidebarProvider({
  children,
  defaultOpen = true,
}: SidebarProviderProps) {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem("dashboard-sidebar-open");
    return stored ? JSON.parse(stored) : defaultOpen;
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    localStorage.setItem("dashboard-sidebar-open", JSON.stringify(isOpen));
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "b" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <SidebarContext.Provider value={{ isOpen, setIsOpen, isMobile, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// sidebar component
// ---------------------------------------------------------------------------

interface DashboardSidebarProps {
  activeView: ActiveView;
  onViewChange: (view: ActiveView) => void;
  sidebarItems: SidebarItem[];
  isBlogmaster: boolean;
  isTrainee?: boolean;
  isAdmin?: boolean;
  isPropertiesTabOnly?: boolean;
}

export function DashboardSidebar({
  activeView,
  onViewChange,
  sidebarItems,
  isBlogmaster,
  isTrainee = false,
  isAdmin = false,
  isPropertiesTabOnly = false,
}: DashboardSidebarProps) {
  const { isOpen, setIsOpen, isMobile, toggle } = useSidebarContext();

  const filteredItems = sidebarItems.filter((item) => {
    // blogmaster can only see blogs
    if (isBlogmaster) {
      return item.id === "blogs";
    }
    // some agents only use the properties list
    if (isPropertiesTabOnly) {
      return item.id === "properties";
    }
    // trainee can only see overview, properties, contacts, and drafts
    if (isTrainee) {
      return (
        item.id === "overview" ||
        item.id === "properties" ||
        item.id === "contacts" ||
        item.id === "drafts"
      );
    }
    // users section is admin-only
    if (item.id === "users" && !isAdmin) {
      return false;
    }
    // others cannot see blogs
    return item.id !== "blogs";
  });

  const handleItemClick = (itemId: ActiveView) => {
    onViewChange(itemId);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const sidebarContent = (
    <div className="flex h-full flex-col bg-card border-r">
      {/* header */}
      <div className="flex h-12 items-center gap-2 border-b px-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary">
          <Building className="h-4 w-4 text-primary-foreground" />
        </div>
        {isOpen && (
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold">Staff Web</h1>
            <p className="truncate text-xs text-muted-foreground">
              Property Management
            </p>
          </div>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="h-7 w-7 shrink-0"
          >
            {isOpen ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <Menu className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      {/* navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {filteredItems.map((item) => {
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={cn(
                  "hit-area-1 flex w-full select-none items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-all duration-150 ease-out active:scale-[0.97] cursor-pointer",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <span className="shrink-0">{item.icon}</span>
                {isOpen && (
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {item.label}
                    </div>
                    <div className="truncate text-xs opacity-75">
                      {item.description}
                    </div>
                  </div>
                )}
                {isOpen && isActive && (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* footer */}
      <div className="border-t p-2">
        <UserProfile collapsed={!isOpen} />
        {isOpen && (
          <div className="mt-2 text-center text-xs text-muted-foreground">
            v{APP_VERSION}
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="left" className="w-64 p-0 [&>button]:hidden">
            {sidebarContent}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div
      className={cn(
        "relative shrink-0 border-r bg-card transition-all duration-200",
        isOpen ? "w-56" : "w-14"
      )}
    >
      {sidebarContent}
    </div>
  );
}
